import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { useT } from "../hooks/useT";

export default function Navigation() {
  const { lang, toggle } = useLang();
  const t = useT();
  const [scrollY, setScrollY] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const navContainerRef = useRef(null);
  const indicatorRef = useRef(null);

  const navLinks = [
    { label: t.nav.mandate, href: "#mandate" },
    { label: t.nav.vision, href: "#vision" },
    { label: t.nav.operations, href: "#platform" },
    { label: t.nav.governance, href: "#governance" },
    { label: t.nav.inquiry, href: "#inquiry" },
  ];

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Active section detection
  useEffect(() => {
    const sectionIds = ["mandate", "vision", "platform", "governance", "inquiry"];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection("#" + entry.target.id);
          }
        });
      },
      { rootMargin: "-40% 0px -40% 0px", threshold: 0 }
    );
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  // Move indicator to active link
  const updateIndicator = useCallback(() => {
    if (!navContainerRef.current || !indicatorRef.current || !activeSection) {
      if (indicatorRef.current) indicatorRef.current.style.opacity = "0";
      return;
    }
    const activeLink = navContainerRef.current.querySelector(`a[href="${activeSection}"]`);
    if (activeLink) {
      const containerRect = navContainerRef.current.getBoundingClientRect();
      const linkRect = activeLink.getBoundingClientRect();
      indicatorRef.current.style.left = `${linkRect.left - containerRect.left}px`;
      indicatorRef.current.style.width = `${linkRect.width}px`;
      indicatorRef.current.style.opacity = "1";
    }
  }, [activeSection]);

  useEffect(() => {
    updateIndicator();
  }, [activeSection, updateIndicator]);

  const scrolled = scrollY > 60;
  const logoOpacity = Math.min(1, Math.max(0, (scrollY - 100) / 200));

  const handleClick = (e, href) => {
    e.preventDefault();
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-200 ${
        scrolled ? "bg-[#0a0a0a]/[0.97] border-b" : "bg-transparent border-b border-transparent"
      }`}
      style={{ borderColor: scrolled ? 'var(--rule)' : 'transparent' }}
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 flex items-center justify-between h-14">
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          data-testid="nav-logo"
          className="font-serif text-[13px] tracking-[0.3em] uppercase text-[var(--text-primary)]"
          style={{ fontWeight: 500, opacity: logoOpacity, transition: 'opacity 0.3s ease' }}
        >
          Leon Galt
        </a>

        <div ref={navContainerRef} className="hidden md:flex items-center gap-10 relative">
          {/* Active section sliding indicator */}
          <div
            ref={indicatorRef}
            className="absolute bottom-0 h-px"
            style={{
              backgroundColor: 'var(--gold)',
              opacity: 0,
              transition: 'left 0.4s cubic-bezier(0.16, 1, 0.3, 1), width 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease',
            }}
          />
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleClick(e, link.href)}
              data-testid={`nav-${link.href.replace("#", "")}`}
              className={`nav-link font-sans text-[11px] uppercase tracking-[0.18em] transition-colors duration-150 hover:text-[var(--text-primary)] ${activeSection === link.href ? 'text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'}`}
            >
              {link.label}
            </a>
          ))}

          <Link
            to="/dantes"
            className="font-sans text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] transition-colors duration-150 hover:text-[var(--text-primary)]"
          >
            Dantès
          </Link>

          <button
            onClick={toggle}
            data-testid="language-toggle"
            className="font-sans text-[11px] tracking-[0.15em] text-[var(--text-tertiary)] transition-colors duration-150 hover:text-[var(--text-primary)] ml-2"
          >
            <span className={lang === "en" ? "text-[var(--text-primary)]" : ""}>EN</span>
            <span className="mx-1.5 text-[var(--rule-strong)]">/</span>
            <span className={lang === "fr" ? "text-[var(--text-primary)]" : ""}>FR</span>
          </button>
        </div>

        <div className="flex md:hidden items-center gap-4">
          <button
            onClick={toggle}
            data-testid="language-toggle-mobile"
            className="font-sans text-[11px] tracking-[0.15em] text-[var(--text-tertiary)]"
          >
            {lang === "en" ? "FR" : "EN"}
          </button>
          <button
            data-testid="mobile-menu-button"
            className="text-[var(--text-tertiary)] transition-colors duration-150 hover:text-[var(--text-primary)]"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-expanded={menuOpen}
            aria-label="Toggle menu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
              {menuOpen ? (
                <>
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="18" y1="6" x2="6" y2="18" />
                </>
              ) : (
                <>
                  <line x1="4" y1="8" x2="20" y2="8" />
                  <line x1="4" y1="16" x2="20" y2="16" />
                </>
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Animated mobile menu */}
      <div
        className={`md:hidden bg-[#0a0a0a] border-b mobile-menu-enter ${menuOpen ? 'mobile-menu-open' : ''}`}
        style={{ borderColor: menuOpen ? 'var(--rule)' : 'transparent' }}
      >
        <div className="px-6 py-6 flex flex-col gap-5">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleClick(e, link.href)}
              className="nav-link font-sans text-[11px] uppercase tracking-[0.18em] text-[var(--text-tertiary)] transition-colors duration-150 hover:text-[var(--text-primary)]"
            >
              {link.label}
            </a>
          ))}
          <Link
            to="/dantes"
            className="nav-link font-sans text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] transition-colors duration-150 hover:text-[var(--text-primary)]"
            onClick={() => setMenuOpen(false)}
          >
            Dantès
          </Link>
        </div>
      </div>
    </nav>
  );
}
